import React, { useEffect, useRef, useState, useCallback } from 'react'
import PropTypes from 'prop-types'

const GMAPS_API_KEY = import.meta.env.SNOWPACK_GMAPS_API_KEY

function MapComponent({ center, onLocationSelect, zoom }) {
  const mapRef = useRef(null)
  const markerRef = useRef(null)
  const [map, setMap] = useState(null)

  useEffect(() => {
    if (mapRef.current && !map && window.google) {
      const mapInstance = new window.google.maps.Map(mapRef.current, {
        center,
        zoom,
        mapTypeId: 'hybrid',
        mapId: '4f74a6b84d257dabc24eb7c3',
        mapTypeControl: false,
        zoomControl: true,
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.RIGHT_CENTER,
        },
        scaleControl: true,
        streetViewControl: false,
        fullscreenControl: false,
      })
      setMap(mapInstance)
    }
  }, [mapRef, map, center, zoom])

  useEffect(() => {
    if (!map) return

    const clickListener = map.addListener('click', (event) => {
      if (event.latLng) {
        const lat = event.latLng.lat()
        const lng = event.latLng.lng()
        onLocationSelect(lat, lng)
      }
    })

    return () => {
      window.google.maps.event.removeListener(clickListener)
    }
  }, [map, onLocationSelect])

  useEffect(() => {
    if (!map || !window.google || !center) return

    map.setCenter(center)

    if (center.lat !== 0 && center.lng !== 0) {
      if (window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement) {
        if (!markerRef.current) {
          markerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
            position: center,
            map,
            gmpDraggable: true,
            title: 'Location Selected',
          })

          const dragListener = markerRef.current.addListener('dragend', () => {
            if (markerRef.current && markerRef.current.position) {
              const position = markerRef.current.position
              onLocationSelect(position.lat, position.lng)
            }
          })

          return () => {
            window.google.maps.event.removeListener(dragListener)
          }
        } else {
          if ('position' in markerRef.current) {
            markerRef.current.position = center
          }
        }
      } else if (window.google.maps.Marker) {
        if (!markerRef.current) {
          markerRef.current = new window.google.maps.Marker({
            position: center,
            map,
            draggable: true,
            title: 'Location Selected',
          })

          const dragListener = markerRef.current.addListener('dragend', () => {
            if (markerRef.current) {
              const position = markerRef.current.getPosition()
              if (position) {
                onLocationSelect(position.lat(), position.lng())
              }
            }
          })

          return () => {
            window.google.maps.event.removeListener(dragListener)
          }
        } else {
          markerRef.current.setPosition(center)
        }
      }
    }
  }, [center, map, onLocationSelect])

  return React.createElement('div', { 
    ref: mapRef, 
    style: { width: '100%', height: '100%' } 
  })
}

MapComponent.propTypes = {
  center: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired
  }).isRequired,
  onLocationSelect: PropTypes.func.isRequired,
  zoom: PropTypes.number.isRequired
}

export default function GoogleMapPicker({
  center = { lat: -14.235, lng: -51.9253 },
  zoom = 4,
  onLocationSelect,
  height = '300px',
}) {
  const [mapCenter, setMapCenter] = useState(center)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isError, setIsError] = useState(false)

  const handleLocationSelect = useCallback((lat, lng) => {
    setMapCenter({ lat, lng })
    onLocationSelect(lat, lng)
  }, [onLocationSelect])

  useEffect(() => {
    const loadGoogleMaps = () => {
      if (window.google) {
        setIsLoaded(true)
        return
      }

      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GMAPS_API_KEY}&libraries=places,marker`
      script.async = true
      script.defer = true
      script.onload = () => setIsLoaded(true)
      script.onerror = () => setIsError(true)
      document.head.appendChild(script)
    }

    loadGoogleMaps()
  }, [])

  const containerStyle = { 
    height,
    width: '100%',
    border: '1px solid #ddd',
    borderRadius: '4px',
    overflow: 'hidden',
    position: 'relative',
    background: '#f6f8fa'
  }

  if (isError) {
    return React.createElement('div', {
      style: {
        ...containerStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }
    }, React.createElement('div', {
      style: { color: '#dc3545' }
    }, 'Error loading map'))
  }

  if (!isLoaded) {
    return React.createElement('div', {
      style: {
        ...containerStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }
    }, React.createElement('div', {
      style: { color: '#6c757d' }
    }, 'Loading maps...'))
  }

  return React.createElement('div', {
    style: containerStyle
  }, React.createElement(MapComponent, {
    center: mapCenter,
    zoom,
    onLocationSelect: handleLocationSelect
  }))
}

GoogleMapPicker.propTypes = {
  center: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number
  }),
  zoom: PropTypes.number,
  onLocationSelect: PropTypes.func.isRequired,
  height: PropTypes.string
}